import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle, ShieldCheck, Flame } from "lucide-react";
import { IMAGES } from "@/data/site";
import { useSite } from "@/hooks/content";

const EASE = [0.22, 1, 0.36, 1];
const LINES = ["Safer Workplaces.", "Better Prepared", "Organisations."];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: EASE } }),
};

export default function Hero() {
  const { waLink } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const yFloat = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-surface pt-[84px] lg:pt-[104px]" data-testid="hero-section">
      {/* Ambient blobs + blueprint */}
      <div className="pointer-events-none absolute inset-0 bg-blueprint opacity-60" />
      <div className="pointer-events-none absolute -left-32 top-20 h-[26rem] w-[26rem] rounded-full bg-brand-light/25 blur-[130px]" />
      <div className="pointer-events-none absolute right-0 top-40 h-96 w-96 rounded-full bg-brand/10 blur-[120px]" />

      <div className="container-sky relative grid items-center gap-12 py-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:py-24">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-white px-4 py-2 shadow-sm"
          >
            <span className="flex h-2 w-2 animate-pulse rounded-full bg-brand" />
            <span className="tech-tag text-navy">WSH &amp; Fire Safety • Singapore</span>
          </motion.div>

          <h1 className="mt-6 font-heading text-[2.6rem] font-extrabold leading-[1.04] tracking-tight text-navy sm:text-6xl lg:text-[4.2rem]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="show"
                  className={`block ${i === 1 ? "text-gradient-sky" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Professional Workplace Safety &amp; Health and Fire Safety consultancy, training and
            emergency preparedness solutions for organisations in Singapore.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-col gap-3.5 sm:flex-row sm:flex-wrap"
          >
            <Link to="/contact" data-testid="hero-cta-quote" className="btn-primary-sky">
              Request a Quote <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/services" data-testid="hero-cta-services" className="btn-outline-sky">
              Explore Our Services
            </Link>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp" className="btn-whatsapp">
              <MessageCircle className="h-4 w-4" /> WhatsApp Us
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4"
          >
            {[
              { icon: ShieldCheck, label: "Workplace Safety & Health" },
              { icon: Flame, label: "Fire Safety & Emergency Preparedness" },
            ].map((it) => (
              <div key={it.label} className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand">
                  <it.icon className="h-5 w-5" />
                </span>
                <span className="text-sm font-semibold text-navy">{it.label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.35, ease: EASE }}
          className="relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden rounded-[1.8rem] shadow-[0_30px_80px_rgba(3,56,106,0.28)] sm:aspect-[5/6]">
            <motion.img
              style={{ y: yImg }}
              src={IMAGES.hero}
              alt="Professional workplace safety and fire safety training in a modern Singapore facility"
              className="h-[112%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/50 via-transparent to-transparent" />
            {/* clipped frame corner */}
            <div className="absolute left-5 top-5 h-12 w-12 rounded-tl-2xl border-l-2 border-t-2 border-white/70" />
            <div className="absolute bottom-5 right-5 h-12 w-12 rounded-br-2xl border-b-2 border-r-2 border-white/70" />
          </div>

          {/* Floating stat card */}
          <motion.div
            style={{ y: yFloat }}
            className="absolute -bottom-6 -left-4 w-52 rounded-2xl border border-slate-100 bg-white/95 p-5 shadow-[0_18px_50px_rgba(3,56,106,0.18)] backdrop-blur sm:-left-8"
          >
            <p className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">Our Method</p>
            <p className="mt-2 font-heading text-lg font-bold text-navy">Assess · Advise</p>
            <p className="font-heading text-lg font-bold text-brand-ink">Implement · Improve</p>
          </motion.div>

          {/* Arrow trajectory */}
          <svg className="pointer-events-none absolute -right-6 -top-8 h-28 w-28 text-brand sm:h-36 sm:w-36" viewBox="0 0 120 120" fill="none">
            <motion.path
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.6, delay: 0.8, ease: EASE }}
              d="M10 100 Q60 90 95 30" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="1 8"
            />
            <motion.path
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }}
              d="M95 30 L78 34 M95 30 L91 47" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round"
            />
          </svg>
        </motion.div>
      </div>
    </section>
  );
}
